import React, { useState, useEffect, useContext } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, CircularProgress } from '@mui/material';
import { getTheme } from './styles/theme';
import NotesIcon from '@mui/icons-material/Notes';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import WelcomeScreen from './components/auth/WelcomeScreen';
import Sidebar from './components/common/Sidebar';
import TodoList from './components/lists/TodoListEnhanced';
import BirthdayList from './components/lists/BirthdayList';
import ExpenseList from './components/lists/ExpenseList';
import FoodList from './components/lists/FoodList';
import WellnessLog from './components/lists/WellnessLog';
import RecipeList from './components/lists/RecipeList';
import Dashboard from './components/common/Dashboard';
import { getUserLists, addList, updateList, deleteList } from './utils/firebase';
import { IconButton, Dialog, DialogTitle, DialogContent, ImageList, ImageListItem, ImageListItemBar, Menu, MenuItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';

// Curated Unsplash nature/zen images (free to use via Unsplash API)
const BACKGROUND_IMAGES = [
  {
    url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
    photographer: 'Ivana Cajina',
    link: 'https://unsplash.com/@von_co'
  },
  {
    url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80',
    photographer: 'Casey Horner',
    link: 'https://unsplash.com/@mischievous_penguins'
  },
  {
    url: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1920&q=80',
    photographer: 'Lukasz Szmigiel',
    link: 'https://unsplash.com/@szmigieldesign'
  },
  {
    url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1920&q=80',
    photographer: 'v2osk',
    link: 'https://unsplash.com/@v2osk'
  },
  {
    url: 'https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?w=1920&q=80',
    photographer: 'Sergey Pesterev',
    link: 'https://unsplash.com/@sickle'
  },
  {
    url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80',
    photographer: 'Nathan Anderson',
    link: 'https://unsplash.com/@nathananderson'
  },
  {
    url: 'https://images.unsplash.com/photo-1426604966848-d7adac402bff?w=1920&q=80',
    photographer: 'Casey Horner',
    link: 'https://unsplash.com/@mischievous_penguins'
  },
];

const MainApp = () => {
  const { user, loading, logout } = useContext(AuthContext);
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [bgDialogOpen, setBgDialogOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  const theme = getTheme('dark');

  // Select random background on mount and change daily
  useEffect(() => {
    const savedBgIndex = localStorage.getItem('backgroundImageIndex');
    const savedDate = localStorage.getItem('backgroundDate');
    const today = new Date().toDateString();

    if (savedBgIndex !== null && savedDate === today) {
      setBackgroundImage(BACKGROUND_IMAGES[parseInt(savedBgIndex)]);
    } else {
      const randomIndex = Math.floor(Math.random() * BACKGROUND_IMAGES.length);
      setBackgroundImage(BACKGROUND_IMAGES[randomIndex]);
      localStorage.setItem('backgroundImageIndex', randomIndex.toString());
      localStorage.setItem('backgroundDate', today);
    }
  }, []);

  useEffect(() => {
    if (user) {
      console.log('Fetching lists for user:', user.uid);
      const unsubscribe = getUserLists(user.uid, (userLists) => {
        console.log('Received lists:', userLists);
        setLists(userLists);

        setSelectedListId((currentSelectedId) => {
          if (!currentSelectedId && userLists.length > 0) {
            return 'home';
          }

          if (currentSelectedId && currentSelectedId !== 'home' && currentSelectedId !== 'past-days' && !userLists.find(l => l.id === currentSelectedId)) {
            return 'home';
          }

          return currentSelectedId;
        });
      });

      return () => unsubscribe();
    }
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAddList = async (name, type) => {
    if (user) {
      const newListId = await addList(user.uid, name, type);
      setSelectedListId(newListId);
    }
  };

  const handleDeleteList = async (listId) => {
    if (window.confirm('Are you sure you want to delete this list?')) {
      await deleteList(listId);
      if (selectedListId === listId) {
        setSelectedListId(lists.length > 1 ? lists[0].id : null);
      }
    }
  };

  const handleUpdateList = async (listId, updates) => {
    console.log('Updating list:', listId, updates);
    try {
      await updateList(listId, updates);
      console.log('List updated successfully');
    } catch (error) {
      console.error('Error updating list:', error);
      throw error;
    }
  };

  const handleBackgroundChange = (index) => {
    setBackgroundImage(BACKGROUND_IMAGES[index]);
    localStorage.setItem('backgroundImageIndex', index.toString());
    setBgDialogOpen(false);
  };

  const selectedList = selectedListId === 'home' ? null : lists.find(list => list.id === selectedListId);
  const activeLists = lists.filter(l => !l.archived);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: 'background.default',
            gap: 3
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <NotesIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
          </Box>
          <Typography variant="h6" color="text.secondary">
            Loading Notepad...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <WelcomeScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          flexDirection: 'column',
          backgroundImage: backgroundImage ? `url(${backgroundImage.url})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            zIndex: 0
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
          {/* Hamburger Menu - Top Left (Mobile Only) */}
          <Box sx={{ position: 'fixed', top: 16, left: 16, zIndex: 1100, display: { xs: 'block', sm: 'none' } }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              <MenuIcon sx={{ color: 'white', fontSize: 32 }} />
            </IconButton>
          </Box>

          {/* Profile Icon - Top Right */}
          <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1100 }}>
            <IconButton
              onClick={(e) => setProfileAnchorEl(e.currentTarget)}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
              }}
            >
              <AccountCircleIcon sx={{ color: 'white', fontSize: 32 }} />
            </IconButton>
            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={() => setProfileAnchorEl(null)}
              PaperProps={{
                sx: {
                  backgroundColor: 'rgba(30, 30, 30, 0.95)',
                  backdropFilter: 'blur(10px)'
                }
              }}
            >
              <MenuItem disabled>
                <ListItemText
                  primary={user?.email}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </MenuItem>
              <MenuItem onClick={async () => {
                setProfileAnchorEl(null);
                await logout();
              }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <Sidebar
              lists={lists}
              selectedListId={selectedListId}
              onSelectList={setSelectedListId}
              onAddList={handleAddList}
              onDeleteList={handleDeleteList}
              onUpdateList={handleUpdateList}
              mobileOpen={mobileOpen}
              onDrawerToggle={handleDrawerToggle}
            />

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: { xs: 1, sm: 3 },
                pt: { xs: '70px', sm: 3 },
                pb: { xs: '80px', sm: 3 },
                width: { sm: `calc(100% - 280px)` },
                overflow: 'auto'
              }}
            >
              {selectedListId === 'home' ? (
                <Dashboard
                  lists={activeLists}
                  onSelectList={setSelectedListId}
                  showPastDays={false}
                  backgroundImage={backgroundImage}
                  onBackgroundClick={() => setBgDialogOpen(true)}
                />
              ) : selectedListId === 'past-days' ? (
                <WellnessLog />
              ) : (
                <Box sx={{ maxWidth: { xs: '100%', sm: 'lg' }, mx: 'auto', width: '100%' }}>
                  {!selectedList ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h5" color="text.secondary">
                        No lists yet. Create your first list!
                      </Typography>
                    </Box>
                  ) : selectedList.type === 'todo' ? (
                    <TodoList list={selectedList} onUpdateList={handleUpdateList} />
                  ) : selectedList.type === 'birthday' ? (
                    <BirthdayList list={selectedList} onUpdateList={handleUpdateList} />
                  ) : selectedList.type === 'expense' ? (
                    <ExpenseList list={selectedList} onUpdateList={handleUpdateList} />
                  ) : selectedList.type === 'food' ? (
                    <FoodList list={selectedList} onUpdateList={handleUpdateList} />
                  ) : selectedList.type === 'recipe' ? (
                    <RecipeList list={selectedList} onUpdateList={handleUpdateList} />
                  ) : null}
                </Box>
              )}
            </Box>
          </Box>

          {/* Footer with Background Button and Credits - Fixed at bottom */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: { xs: 0, sm: '280px' },
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: { xs: 3, sm: 4 },
              py: 1.5,
              zIndex: 100
            }}
          >
            <Button
              startIcon={<WallpaperIcon />}
              onClick={() => setBgDialogOpen(true)}
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Change Background
            </Button>
          </Box>
        </Box>

        {/* Background Selector Dialog */}
        <Dialog
          open={bgDialogOpen}
          onClose={() => setBgDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 30, 30, 0.95)',
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          <DialogTitle>Choose Background</DialogTitle>
          <DialogContent>
            <ImageList cols={3} gap={12} sx={{ mt: 1 }}>
              {BACKGROUND_IMAGES.map((img, index) => (
                <ImageListItem
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    border: backgroundImage === BACKGROUND_IMAGES[index] ? '3px solid #00897b' : '3px solid transparent',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 24px rgba(0, 137, 123, 0.3)'
                    }
                  }}
                  onClick={() => handleBackgroundChange(index)}
                >
                  <img
                    src={`${img.url.replace('w=1920', 'w=300')}`}
                    alt={`Background ${index + 1}`}
                    loading="lazy"
                    style={{ height: '150px', objectFit: 'cover' }}
                  />
                  <ImageListItemBar
                    title={img.photographer}
                    sx={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)'
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
